import axios from 'axios';
import { getToken } from 'firebase/app-check';
import { appCheck } from '../config/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'https://aps-backend-219066752787.us-central1.run.app/api';
console.log('[API_URL Debug]', API_URL);

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add App Check token and Auth token to requests
api.interceptors.request.use(
    async (config) => {
        // Add App Check token
        if (appCheck) {
            try {
                const appCheckTokenResponse = await getToken(appCheck, false);
                config.headers['X-Firebase-AppCheck'] = appCheckTokenResponse.token;
            } catch (err) {
                console.error('App Check token fetch failed:', err.message);
                // Do NOT reload — a reload causes initAuth to re-run, which calls
                // /users/profile without App Check, triggering the response interceptor
                // to log the user out. Simply continue without the header; if the
                // backend enforces App Check, individual requests will fail gracefully.
            }
        }

        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Subscription Payments API (Needed for Admin components)
export const subscriptionPaymentsAPI = {
    getAll: async (filters = {}) => {
        const response = await api.get('/admin/subscription-payments', { params: filters });
        return response.data;
    },
    approve: async (id) => {
        const response = await api.put(`/admin/subscription-payments/${id}/approve`);
        return response.data;
    },
    reject: async (id, reason) => {
        const response = await api.put(`/admin/subscription-payments/${id}/reject`, { reason });
        return response.data;
    },
    getHistory: async () => {
        const response = await api.get('/subscriptions/history');
        return response.data;
    },
    getStatus: async () => {
        const response = await api.get('/subscriptions/status');
        return response.data;
    },
    createPortalSession: async () => {
        const response = await api.post('/stripe/create-portal-session');
        return response.data;
    }
};

// Bookings API
export const bookingsAPI = {
    getAll: async (params) => {
        const response = await api.get('/bookings', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/bookings/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/bookings', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/bookings/${id}`, data);
        return response.data;
    },
    confirm: async (id, data = {}) => {
        const response = await api.put(`/bookings/${id}/confirm`, data);
        return response.data;
    },
    cancel: async (id, reason) => {
        const response = await api.delete(`/bookings/${id}`, { data: { reason } });
        return response.data;
    },
    convertToTenant: async (id, data) => {
        const response = await api.post(`/bookings/${id}/convert`, data);
        return response.data;
    },
    checkConflicts: async (data) => {
        const response = await api.post('/bookings/check-conflicts', data);
        return response.data;
    },
    getStats: async (params) => {
        const response = await api.get('/bookings/stats', { params });
        return response.data;
    }
};

// Waitlist API
export const waitlistAPI = {
    getAll: async (params) => {
        const response = await api.get('/waitlist', { params });
        return response.data;
    },
    add: async (data) => {
        const response = await api.post('/waitlist', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/waitlist/${id}`, data);
        return response.data;
    },
    remove: async (id, reason) => {
        const response = await api.delete(`/waitlist/${id}`, { data: { reason } });
        return response.data;
    },
    process: async (id, action) => {
        const response = await api.post(`/waitlist/${id}/process`, { action });
        return response.data;
    }
};

// Tenants API
export const tenantsAPI = {
    getAll: async (params) => {
        const response = await api.get('/tenants', { params });
        return response.data;
    },
    getById: async (id) => {
        const response = await api.get(`/tenants/${id}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/tenants', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/tenants/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/tenants/${id}`);
        return response.data;
    },
    endLease: async (id) => {
        const response = await api.post(`/tenants/${id}/end-lease`);
        return response.data;
    },
    extendLease: async (id, newEndDate) => {
        const response = await api.post(`/tenants/${id}/extend`, { newEndDate });
        return response.data;
    },
    renewLease: async (id, data) => {
        const response = await api.post(`/tenants/${id}/renew`, data);
        return response.data;
    },
    transferApartment: async (id, data) => {
        const response = await api.post(`/tenants/${id}/transfer`, data);
        return response.data;
    },
    getRentPayments: async (id, params) => {
        const response = await api.get(`/tenants/leases/${id}/payments`, { params });
        return response.data;
    }
};

export default api;

